import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { EntityLabel } from "components/AddProductToCart/AddProductToCart";
import { FormikForm } from "components/FormikForm/FormikForm";
import { PriceInput } from "components/PriceInput/PriceInput";
import ToolTip from "components/design/toolTip";
import { useAppSelector } from "hooks/useAppSelector";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { FRACTIONAL_PAYROLL_ADMINISTRATOR } from "constants/addons";
import { useAddProductToCartMutation } from "store/apis/products";
import { useGetSubscriptionsQuery } from "store/apis/subscriptions";
import {
  setAddonData,
  setAddonPayload,
  setAddonType,
  setOpenCheckoutModal,
} from "store/slices/addons";
import { setPaymentTitle, setUsableCredit } from "store/slices/credit";
import { RootState } from "store/store";
import { BackendError } from "types/utils/error";

type FractionalPayrollAdministratorFormValues = {
  amount: number;
};

export const FractionalPayrollAdministrator = () => {
  const { alertToast, successToast } = useToast();
  const dispatch = useDispatch();
  const { isCustomer } = useRoleBasedView();
  const { addonData } = useSelector((state: RootState) => state.addonsSlice);
  const { uuid: groupId, entities } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const selectedEntity = entities?.find((entity) => entity.uuid === entityId);

  const [showAddonModal, setShowAddonModal] = useState<boolean>(
    addonData?.subscription_type === FRACTIONAL_PAYROLL_ADMINISTRATOR
  );

  const { subscription_name, amount, stripe_coupon, uuid } = addonData || {};

  const updatedAmount = stripe_coupon?.discounted_amount.toString() || amount;

  const isCartSubscriptionActive = useAppSelector(
    (state) => state.cartSubscription.isCartSubscriptionActive
  );

  const { data: subscriptions } = useGetSubscriptionsQuery(
    { groupId, entityId },
    { skip: !groupId || !entityId }
  );

  const [addToCart, { isLoading: isAddingToCart }] =
    useAddProductToCartMutation();

  const handleClose = () => {
    setShowAddonModal(false);
    dispatch(setAddonData(null));
    dispatch(setAddonType(""));
  };

  const handleAddToCart = async (customAmount: number) => {
    const subscription = subscriptions?.find((s) => s.uuid === uuid);
    if (!groupId || !uuid || !subscription) {
      return;
    }
    try {
      await addToCart({
        groupId,
        entityId,
        payload: {
          product_id: uuid,
          product_content_type_id: subscription.product_content_type_id,
          payment_amount: customAmount,
        },
      }).unwrap();
      successToast({
        message: `${subscription_name} subscription added to cart`,
      });
      handleClose();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ??
          "Something went wrong",
      });
    }
  };

  const handlePayAndSubscribe = (customAmount: number) => {
    dispatch(
      setAddonPayload({
        title: subscription_name,
        amount: updatedAmount,
        uuid,
        type: FRACTIONAL_PAYROLL_ADMINISTRATOR,
        customPrice: customAmount,
      })
    );
    dispatch(setUsableCredit(false));
    dispatch(setPaymentTitle(subscription_name!));
    dispatch(setAddonType(""));
    dispatch(setOpenCheckoutModal());
    setShowAddonModal(false);
  };

  const handleSubmit = (values: FractionalPayrollAdministratorFormValues) => {
    if (isCartSubscriptionActive) {
      void handleAddToCart(values.amount);
      return;
    }
    handlePayAndSubscribe(values.amount);
  };

  return (
    <Modal.Root open={showAddonModal} onOpenChange={handleClose}>
      <Modal.Content useCustomOverlay>
        <FormikForm<FractionalPayrollAdministratorFormValues>
          initialValues={{ amount: Number(updatedAmount) || 0 }}
          onSubmit={handleSubmit}
        >
          <>
            <Modal.Header>
              <Modal.Title>{subscription_name}</Modal.Title>
              <Modal.Close />
            </Modal.Header>
            <Modal.Body>
              <div className="t-grid t-grid-cols-2 t-gap-4">
                <ToolTip text="Change entity from top right of the page">
                  <div>
                    <Combobox
                      isDisabled
                      components={{ DropdownIndicator: () => null }}
                      menuPortalTarget={document.body}
                      name="entity_id"
                      withForm
                      label="Entity"
                      value={
                        selectedEntity && {
                          label: (
                            <EntityLabel
                              name={selectedEntity.name}
                              countryCode={selectedEntity.country_code}
                            />
                          ),
                          value: selectedEntity.uuid,
                        }
                      }
                      options={entities?.map((entity) => ({
                        label: (
                          <EntityLabel
                            name={entity.name}
                            countryCode={entity.country_code}
                          />
                        ),
                        value: entity.uuid,
                      }))}
                    />
                  </div>
                </ToolTip>
                <div className="t-col-span-2">
                  <div className="t-text-body-sm t-text-text-30">About</div>
                  <div className="t-text-body-sm t-text-text-100">
                    We run payroll operations, handle agency notices, and
                    monitor all compliance on top of your existing payroll
                    provider (e.g. Gusto), so nothing depends on someone
                    remembering to check; covers up to 10 employees;
                    registrations and filings charged separately.
                  </div>
                </div>
                <div className="t-col-span-2">
                  {isCustomer ? (
                    <>
                      <div className="t-text-body-sm t-text-text-30">Price</div>
                      <p className="t-text-subtitle">
                        <AmountSuperScript amount={Number(updatedAmount)} />
                      </p>
                    </>
                  ) : (
                    <PriceInput name="amount" label="Price" required />
                  )}
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer className="t-flex t-justify-end t-gap-3">
              <Button type="button" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                customType="primary"
                isLoading={isAddingToCart}
                disabled={isAddingToCart}
              >
                {isCartSubscriptionActive ? "Add to Cart" : "Pay & subscribe"}
              </Button>
            </Modal.Footer>
          </>
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
